import React, { useState, useEffect } from "react";
import { NotebookPen, Sparkles, Loader2, Music2, KeyRound, Gauge, Copy, Check, Plus, Trash2, Quote, Repeat, Lightbulb, AlignLeft } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useStudio } from "@/studio/StudioContext";
import { useAuth } from "@/auth/AuthContext";
import { useSubscription } from "@/subscription/SubscriptionContext";
import { formatApiErrorDetail } from "@/auth/AuthContext";

const LS_KEY = "beatmxr_lyrics";

const ACTIONS = [
  { mode: "hook", label: "Hook", icon: Quote, color: "#ff007f" },
  { mode: "verse", label: "Verse", icon: AlignLeft, color: "#00e5ff" },
  { mode: "rhymes", label: "Rhymes", icon: Repeat, color: "#a855f7" },
  { mode: "cadence", label: "Cadence", icon: Music2, color: "#00ff66" },
  { mode: "ideas", label: "Ideas", icon: Lightbulb, color: "#ffb800" },
  { mode: "continue", label: "Continue", icon: Sparkles, color: "#22d3ee" },
];

export default function Songwriter() {
  const { assistLyrics, bpm, engine } = useStudio();
  const { requireAuth, setShowAuth } = useAuth();
  const { openUpgrade, isPro, refresh } = useSubscription();
  const [open, setOpen] = useState(false);
  const [lyrics, setLyrics] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [theme, setTheme] = useState("");
  const [busy, setBusy] = useState(null); // mode currently running
  const [result, setResult] = useState("");
  const [resultMode, setResultMode] = useState("");
  const [copied, setCopied] = useState(false);

  const songKey = engine?.songKey || "";

  useEffect(() => {
    const id = setTimeout(() => localStorage.setItem(LS_KEY, lyrics), 400);
    return () => clearTimeout(id);
  }, [lyrics]);

  const openPad = () => {
    if (!requireAuth()) return;
    setOpen(true);
  };

  const run = async (mode) => {
    if (mode === "rhymes" && !theme.trim() && !lyrics.trim()) {
      toast.error("Type a word (or a line) to rhyme with first");
      return;
    }
    setBusy(mode);
    setResult("");
    setResultMode(mode);
    try {
      const text = await assistLyrics({ mode, prompt: theme, existing: lyrics });
      setResult(text);
      refresh();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (e.response?.status === 401) setShowAuth(true);
      else if (e.response?.status === 402) {
        setOpen(false);
        openUpgrade(detail);
      } else {
        toast.error(detail || "Could not get a suggestion, try again");
      }
    }
    setBusy(null);
  };

  const insertResult = () => {
    if (!result) return;
    setLyrics((prev) => (prev.trim() ? `${prev.replace(/\s+$/, "")}\n\n${result}` : result));
    toast.success("Added to your pad");
  };

  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {}
  };

  const clearPad = () => {
    setLyrics("");
    setResult("");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="songwriter-open-btn"
          onClick={openPad}
          className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-bold text-black transition-all hover:scale-[1.03]"
          style={{
            background: "linear-gradient(120deg,#7c3aed,#00e5ff)",
            boxShadow: "0 0 14px rgba(124,58,237,0.45)",
          }}
          title="Write lyrics with an AI co-writer that knows your beat"
        >
          <NotebookPen size={14} /> Write
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="bg-[#0b0b0b] border-[#262626] text-white p-0 w-full sm:max-w-[440px] flex flex-col"
        data-testid="songwriter-panel"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#1c1c1c] space-y-0">
          <SheetTitle className="font-display tracking-widest flex items-center gap-2 text-base">
            <NotebookPen size={18} className="text-[#a855f7]" /> SONGWRITER
          </SheetTitle>
          <SheetDescription className="text-neutral-500 text-xs">
            Your notepad + an AI co-writer that knows this beat's tempo &amp; key.
          </SheetDescription>
          <div className="flex items-center gap-2 mt-3">
            <span
              data-testid="songwriter-bpm-chip"
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#00e5ff14] border border-[#00e5ff44] text-[10px] font-mono-data text-[#00e5ff]"
            >
              <Gauge size={11} /> {bpm} BPM
            </span>
            <span
              data-testid="songwriter-key-chip"
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#a855f714] border border-[#a855f744] text-[10px] font-mono-data text-[#c79bff]"
            >
              <KeyRound size={11} /> {songKey || "no key set"}
            </span>
          </div>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 flex flex-col gap-4">
          {/* theme */}
          <input
            data-testid="songwriter-theme-input"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            placeholder="Topic or a word to rhyme — e.g. 'late nights grinding' or 'fire'"
            className="bg-[#050505] border border-[#262626] rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#a855f7] transition-colors"
          />

          {/* assist actions */}
          <div className="grid grid-cols-3 gap-2">
            {ACTIONS.map((a) => {
              const Icon = a.icon;
              const running = busy === a.mode;
              return (
                <button
                  key={a.mode}
                  data-testid={`songwriter-action-${a.mode}`}
                  onClick={() => run(a.mode)}
                  disabled={!!busy}
                  className="flex items-center justify-center gap-1.5 py-2 rounded-sm border text-[11px] font-bold transition-colors disabled:opacity-40"
                  style={{ borderColor: `${a.color}55`, color: a.color }}
                >
                  {running ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />}
                  {a.label}
                </button>
              );
            })}
          </div>

          {/* suggestion */}
          {result && (
            <div
              data-testid="songwriter-result"
              className="rounded-sm border border-[#a855f744] bg-[#a855f70a] p-3 flex flex-col gap-2"
            >
              <div className="flex items-center justify-between">
                <span className="hw-label text-[10px] text-[#c79bff] flex items-center gap-1.5">
                  <Sparkles size={12} /> {resultMode} suggestion
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    data-testid="songwriter-copy-result"
                    onClick={copyResult}
                    className="text-neutral-400 hover:text-white transition-colors"
                    title="Copy"
                  >
                    {copied ? <Check size={14} className="text-[#00ff66]" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-sm text-neutral-200 leading-relaxed">{result}</pre>
              <button
                data-testid="songwriter-insert-result"
                onClick={insertResult}
                className="mt-1 w-full flex items-center justify-center gap-1.5 py-2 rounded-sm text-xs font-bold text-black transition-transform hover:scale-[1.02]"
                style={{ background: "linear-gradient(120deg,#7c3aed,#00e5ff)" }}
              >
                <Plus size={14} /> Add to my pad
              </button>
            </div>
          )}

          {/* the pad */}
          <div className="flex flex-col gap-1.5 flex-1 min-h-[200px]">
            <div className="flex items-center justify-between">
              <span className="hw-label text-[10px] text-neutral-400">Your lyrics</span>
              {lyrics.trim() && (
                <button
                  data-testid="songwriter-clear"
                  onClick={clearPad}
                  className="flex items-center gap-1 text-[10px] text-neutral-500 hover:text-[#ff3333] transition-colors"
                >
                  <Trash2 size={11} /> Clear
                </button>
              )}
            </div>
            <textarea
              data-testid="songwriter-pad"
              value={lyrics}
              onChange={(e) => setLyrics(e.target.value)}
              placeholder="Write your verses and hooks here… tap Hook, Verse or Continue above to get the AI rolling."
              className="flex-1 min-h-[200px] bg-[#050505] border border-[#262626] rounded-sm px-3 py-3 text-sm leading-relaxed outline-none focus:border-[#a855f7] transition-colors resize-none"
            />
          </div>

          {!isPro && (
            <p className="text-[10px] text-neutral-600 text-center">
              Free plan includes a set number of writing assists per day. Pro unlocks unlimited co-writing.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
